// src/pages/DashboardSupervisor.js
import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Paper, Typography, Grid, Card, CardContent,
  Button, Chip, LinearProgress, Alert,
  List, ListItem, ListItemText, ListItemIcon,
  Avatar, IconButton, Tooltip, Badge
} from '@mui/material';
import {
  TrendingUp, People, Schedule, Warning,
  Notifications, CalendarToday, Star,
  ArrowForward, Refresh, PictureAsPdf,
  Engineering, AdminPanelSettings, Group,
  AccessTime, EventBusy, CheckCircle
} from '@mui/icons-material';
import { Link } from 'react-router-dom';

import { getData } from '../services/storageService';
import { useI18n } from '../i18n/i18n';
import { useNotifications } from '../context/NotificationContext';

const DashboardSupervisor = () => {
  const { t } = useI18n();
  const { showNotification } = useNotifications();
  const [dashboardData, setDashboardData] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      const staffData = await getData('staff') || [];
      const attendanceData = await getData('attendance_records') || [];
      const shiftsData = await getData('shift_records') || [];
      const rosterData = await getData('staff_roster') || {};

      const activeStaff = staffData.filter(s => s.status === 'active');
      const today = new Date().toISOString().split('T')[0];
      
      // СЬОГОДНІШНЯ СТАТИСТИКА
      const todayAttendance = attendanceData.filter(a => a.date === today);
      const presentToday = todayAttendance.filter(a => a.status === 'present').length;
      const absentToday = todayAttendance.filter(a => a.status === 'absent').length;
      const lateToday = todayAttendance.filter(a => a.status === 'late').length;

      // ЗАПЛАНОВАНІ ЗМІНИ НА СЬОГОДНІ
      const todayScheduled = Object.values(rosterData).reduce((total, weekRoster) => {
        return total + Object.values(weekRoster).reduce((weekTotal, employeeShifts) => {
          const todayShift = employeeShifts[today];
          return weekTotal + (todayShift && todayShift !== 'OFF' && todayShift !== 'VAC' && todayShift !== 'SICK' ? 1 : 0);
        }, 0);
      }, 0);

      // ПОРУШЕННЯ ГРАФІКУ СЬОГОДНІ
      const todayViolations = Object.values(rosterData).reduce((violations, weekRoster) => {
        Object.entries(weekRoster).forEach(([employeeId, employeeShifts]) => {
          const todayShift = employeeShifts[today];
          if (todayShift && todayShift !== 'OFF' && todayShift !== 'VAC' && todayShift !== 'SICK') {
            const attendanceRecord = todayAttendance.find(a => a.employeeId === parseInt(employeeId));
            if (!attendanceRecord || attendanceRecord.status === 'absent') {
              violations.push({
                employeeId: parseInt(employeeId),
                scheduledShift: todayShift,
                actualStatus: attendanceRecord?.status || 'no record'
              });
            }
          }
        });
        return violations;
      }, []);

      // АКТИВНІ ПРАЦІВНИКИ ЗА ВІДДІЛАМИ
      const staffByDepartment = activeStaff.reduce((acc, employee) => {
        const dept = employee.department || 'Unknown';
        if (!acc[dept]) acc[dept] = [];
        acc[dept].push(employee);
        return acc;
      }, {});

      // НАЙКРАЩІ ВИКОНАВЦІ ЗА ОСТАННІ 7 ДНІВ
      const lastWeekAttendance = attendanceData.filter(a => {
        const recordDate = new Date(a.date);
        const weekAgo = new Date();
        weekAgo.setDate(weekAgo.getDate() - 7);
        return recordDate >= weekAgo;
      });

      const topPerformers = activeStaff.map(employee => {
        const employeeAttendance = lastWeekAttendance.filter(a => a.employeeId === employee.id);
        const presentDays = employeeAttendance.filter(a => a.status === 'present').length;
        const totalDays = employeeAttendance.length;
        const attendanceRate = totalDays > 0 ? (presentDays / totalDays) * 100 : 0;
        
        return {
          ...employee,
          attendanceRate: Math.round(attendanceRate),
          presentDays,
          totalDays
        };
      })
      .filter(emp => emp.totalDays > 0)
      .sort((a, b) => b.attendanceRate - a.attendanceRate)
      .slice(0, 5);

      // НАЙБЛИЖЧІ ВІДПУСТКИ ТА ВІДГУЛИ
      const upcomingTimeOff = attendanceData
        .filter(a => a.status === 'vacation' || a.status === 'day_off')
        .filter(a => new Date(a.date) >= new Date())
        .sort((a, b) => new Date(a.date) - new Date(b.date))
        .slice(0, 5)
        .map(record => {
          const employee = staffData.find(s => s.id === record.employeeId);
          return {
            ...record,
            employeeName: employee ? (employee.name || `${employee.firstName} ${employee.lastName}`) : 'Unknown',
            department: employee?.department
          };
        });

      setDashboardData({
        // Основні показники
        totalStaff: activeStaff.length,
        presentToday,
        absentToday,
        lateToday,
        todayScheduled,
        todayViolations: todayViolations.length,
        
        // Швидкі показники
        attendanceRate: todayScheduled > 0 ? Math.round((presentToday / todayScheduled) * 100) : 0,
        complianceRate: Math.round(((todayScheduled - todayViolations.length) / Math.max(todayScheduled, 1)) * 100),
        
        // Структурні дані
        staffByDepartment,
        topPerformers,
        upcomingTimeOff,
        
        // Останні події
        recentActivities: todayAttendance
          .sort((a, b) => new Date(b.time) - new Date(a.time))
          .slice(0, 8)
          .map(record => {
            const employee = staffData.find(s => s.id === record.employeeId);
            return {
              id: record.id,
              employeeName: employee ? (employee.name || `${employee.firstName} ${employee.lastName}`) : 'Unknown',
              status: record.status,
              time: record.time,
              department: employee?.department
            };
          })
      });

    } catch (error) {
      console.error("Error loading dashboard data:", error);
      showNotification(t('dashboard.load_error'), 'error');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <LinearProgress sx={{ width: '50%' }} />
      </Box>
    );
  }

  const {
    totalStaff,
    presentToday,
    absentToday,
    lateToday,
    todayScheduled,
    todayViolations,
    attendanceRate,
    complianceRate,
    staffByDepartment,
    topPerformers,
    upcomingTimeOff,
    recentActivities
  } = dashboardData;

  return (
    <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto' }}>
      {/* ЗАГОЛОВОК ТА ШВИДКІ ДІЇ */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h3" component="h1" sx={{ fontWeight: 700, mb: 1 }}>
            {t('dashboard.title')}
          </Typography>
          <Typography variant="h6" color="text.secondary">
            {new Date().toLocaleDateString('es-ES', { 
              weekday: 'long', 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Tooltip title={t('dashboard.refresh_data')}>
            <IconButton onClick={loadDashboardData} color="primary">
              <Refresh />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<PictureAsPdf />}
            component={Link}
            to="/analytics"
          >
            {t('dashboard.full_report')}
          </Button>
        </Box>
      </Box>

      {/* ОСНОВНІ ПОКАЗНИКИ СЬОГОДНІ */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* ЗАГАЛЬНА КІЛЬКІСТЬ */}
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 3, height: '100%' }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <People sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {totalStaff}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('dashboard.total_staff')}
                  </Typography>
                </Box>
              </Box>
              <LinearProgress 
                variant="determinate" 
                value={100}
                color="primary"
                sx={{ height: 6, borderRadius: 3 }}
              />
            </CardContent>
          </Card>
        </Grid>

        {/* ПРИСУТНІ СЬОГОДНІ */}
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 3, height: '100%' }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <CheckCircle sx={{ fontSize: 40, color: 'success.main', mr: 2 }} />
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {presentToday}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('dashboard.present_today')}
                  </Typography>
                </Box>
              </Box>
              <LinearProgress 
                variant="determinate" 
                value={attendanceRate}
                color="success"
                sx={{ height: 6, borderRadius: 3 }}
              />
            </CardContent>
          </Card>
        </Grid>

        {/* ВІДСУТНІ СЬОГОДНІ */}
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 3, height: '100%' }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <EventBusy sx={{ fontSize: 40, color: 'error.main', mr: 2 }} />
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {absentToday}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('dashboard.absent_today')}
                  </Typography>
                </Box>
              </Box>
              <LinearProgress 
                variant="determinate" 
                value={absentToday ? (absentToday / todayScheduled) * 100 : 0}
                color="error"
                sx={{ height: 6, borderRadius: 3 }}
              />
            </CardContent>
          </Card>
        </Grid>

        {/* ПОРУШЕННЯ ГРАФІКУ */}
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 3, height: '100%' }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Warning sx={{ fontSize: 40, color: 'warning.main', mr: 2 }} />
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {todayViolations}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('dashboard.schedule_violations')}
                  </Typography>
                </Box>
              </Box>
              <LinearProgress 
                variant="determinate" 
                value={complianceRate}
                color="warning"
                sx={{ height: 6, borderRadius: 3 }}
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* ОСНОВНИЙ КОНТЕНТ */}
      <Grid container spacing={3}>
        {/* СТРУКТУРА ПЕРСОНАЛУ ПО ВІДДІЛАХ */}
        <Grid item xs={12} md={6}>
          <Card sx={{ borderRadius: 3, height: '100%' }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h5" sx={{ fontWeight: 600 }}>
                  {t('dashboard.staff_by_department')}
                </Typography>
                <Button 
                  component={Link} 
                  to="/staff" 
                  endIcon={<ArrowForward />}
                  size="small"
                >
                  {t('dashboard.manage')}
                </Button>
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {Object.entries(staffByDepartment).map(([dept, employees]) => (
                  <Box key={dept} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                    <Box>
                      <Typography variant="body1" fontWeight="500">
                        {dept}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {employees.length} {t('dashboard.employees')}
                      </Typography>
                    </Box>
                    <Chip 
                      label={`${employees.length}`}
                      color="primary"
                      variant="outlined"
                    />
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* НАЙКРАЩІ ВИКОНАВЦІ */}
        <Grid item xs={12} md={6}>
          <Card sx={{ borderRadius: 3, height: '100%' }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h5" sx={{ fontWeight: 600 }}>
                  {t('dashboard.top_performers')}
                </Typography>
                <Button 
                  component={Link} 
                  to="/analytics" 
                  endIcon={<ArrowForward />}
                  size="small"
                >
                  {t('dashboard.view_all')}
                </Button>
              </Box>

              <List>
                {topPerformers.map((employee, index) => (
                  <ListItem key={employee.id} sx={{ px: 0 }}>
                    <ListItemIcon>
                      <Avatar sx={{ 
                        bgcolor: index === 0 ? '#FFD700' : 
                                index === 1 ? '#C0C0C0' : 
                                index === 2 ? '#CD7F32' : 'primary.main',
                        width: 40, 
                        height: 40 
                      }}>
                        {index < 3 ? <Star /> : <People />}
                      </Avatar>
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography variant="body1" fontWeight="500">
                          {employee.name || `${employee.firstName} ${employee.lastName}`}
                        </Typography>
                      }
                      secondary={
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="body2" color="text.secondary">
                            {employee.department}
                          </Typography>
                          <Chip 
                            label={`${employee.attendanceRate}%`}
                            color={
                              employee.attendanceRate >= 95 ? 'success' :
                              employee.attendanceRate >= 85 ? 'warning' : 'error'
                            }
                            size="small"
                          />
                        </Box>
                      }
                    />
                  </ListItem>
                ))}
              </List>

              {(!topPerformers || topPerformers.length === 0) && (
                <Alert severity="info">
                  {t('dashboard.no_performance_data')}
                </Alert>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* ОСТАННІ ПОДІЇ */}
        <Grid item xs={12} md={6}>
          <Card sx={{ borderRadius: 3, height: '100%' }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h5" sx={{ fontWeight: 600 }}>
                  {t('dashboard.recent_activity')}
                </Typography>
                <Button 
                  component={Link} 
                  to="/attendance" 
                  endIcon={<ArrowForward />}
                  size="small"
                >
                  {t('dashboard.view_all')}
                </Button>
              </Box>

              <List>
                {recentActivities.map((activity) => (
                  <ListItem key={activity.id} sx={{ px: 0 }}>
                    <ListItemIcon>
                      <Avatar sx={{ 
                        bgcolor: activity.status === 'present' ? 'success.main' :
                                activity.status === 'absent' ? 'error.main' : 'warning.main',
                        width: 40, 
                        height: 40 
                      }}>
                        {activity.status === 'present' ? <CheckCircle /> :
                         activity.status === 'absent' ? <EventBusy /> : <AccessTime />}
                      </Avatar>
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography variant="body1" fontWeight="500">
                          {activity.employeeName}
                        </Typography>
                      }
                      secondary={
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Chip 
                            label={t(`dashboard.status_${activity.status}`)}
                            color={
                              activity.status === 'present' ? 'success' :
                              activity.status === 'absent' ? 'error' : 'warning'
                            }
                            size="small"
                          />
                          <Typography variant="caption" color="text.secondary">
                            {activity.time ? new Date(activity.time).toLocaleTimeString() : 'N/A'}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                ))}
              </List>

              {recentActivities.length === 0 && (
                <Alert severity="info">
                  {t('dashboard.no_recent_activity')}
                </Alert>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* МАЙБУТНІ ВІДПУСТКИ ТА ВІДГУЛИ */}
        <Grid item xs={12} md={6}>
          <Card sx={{ borderRadius: 3, height: '100%' }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h5" sx={{ fontWeight: 600 }}>
                  {t('dashboard.upcoming_time_off')}
                </Typography>
                <Button 
                  component={Link} 
                  to="/roster" 
                  endIcon={<ArrowForward />}
                  size="small"
                >
                  {t('dashboard.manage')}
                </Button>
              </Box>

              <List>
                {upcomingTimeOff.map((timeOff, index) => (
                  <ListItem key={index} sx={{ px: 0 }}>
                    <ListItemIcon>
                      <Avatar sx={{ 
                        bgcolor: timeOff.status === 'vacation' ? 'info.main' : 'warning.main',
                        width: 40, 
                        height: 40 
                      }}>
                        <CalendarToday />
                      </Avatar>
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography variant="body1" fontWeight="500">
                          {timeOff.employeeName}
                        </Typography>
                      }
                      secondary={
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="body2" color="text.secondary">
                            {new Date(timeOff.date).toLocaleDateString()}
                          </Typography>
                          <Chip 
                            label={t(`dashboard.${timeOff.status}`)}
                            color={timeOff.status === 'vacation' ? 'info' : 'warning'}
                            size="small"
                          />
                        </Box>
                      }
                    />
                  </ListItem>
                ))}
              </List>

              {upcomingTimeOff.length === 0 && (
                <Alert severity="success">
                  {t('dashboard.no_upcoming_time_off')}
                </Alert>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* ШВИДКІ ДІЇ ВНИЗУ */}
      <Card sx={{ mt: 3, p: 3, borderRadius: 3 }}>
        <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
          {t('dashboard.quick_actions')}
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <Button
              fullWidth
              variant="contained"
              startIcon={<Group />}
              component={Link}
              to="/staff"
              sx={{ py: 2, borderRadius: 2 }}
            >
              {t('dashboard.manage_staff')}
            </Button>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Button
              fullWidth
              variant="contained"
              startIcon={<Schedule />}
              component={Link}
              to="/roster"
              sx={{ py: 2, borderRadius: 2 }}
            >
              {t('dashboard.manage_roster')}
            </Button>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Button
              fullWidth
              variant="contained"
              startIcon={<CalendarToday />}
              component={Link}
              to="/attendance"
              sx={{ py: 2, borderRadius: 2 }}
            >
              {t('dashboard.attendance_tracking')}
            </Button>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Button
              fullWidth
              variant="contained"
              startIcon={<PictureAsPdf />}
              component={Link}
              to="/analytics"
              sx={{ py: 2, borderRadius: 2 }}
            >
              {t('dashboard.generate_reports')}
            </Button>
          </Grid>
        </Grid>
      </Card>
    </Box>
  );
};

export default DashboardSupervisor;